import React, { useState, useEffect } from 'react';
import { facilityAPI } from '../services/api';
import api from '../services/api';
import { Shield, Plus, ChevronDown, ChevronUp, Trash2, AlertCircle, CheckCircle, XCircle, Loader, Building2, Zap } from 'lucide-react';
import { toast } from 'react-toastify';

const SOURCE_TYPES = [
  'Boiler - Natural Gas', 'Boiler - Oil', 'Boiler - Coal', 'Boiler - Biomass',
  'Emergency Generator - Diesel', 'Emergency Generator - Natural Gas',
  'Combustion Turbine - Natural Gas', 'Combustion Turbine - Oil',
  'Reciprocating Engine - Diesel', 'Reciprocating Engine - Natural Gas',
  'Storage Tank - Gasoline', 'Storage Tank - Crude Oil', 'Storage Tank - Chemical',
  'Paint Booth', 'Surface Coating', 'Solvent Cleaning',
  'Process Heater', 'Incinerator', 'Flare', 'Cooling Tower',
  'Fugitive Emissions', 'Other'
];

const FUEL_TYPES = [
  'Natural Gas', 'Diesel / No. 2 Fuel Oil', 'No. 6 Fuel Oil',
  'Propane', 'Gasoline', 'Coal', 'Biomass / Wood', 'Waste Oil', 'None / N/A'
];

function ConfidenceBadge({ confidence }) {
  const styles = {
    high: { bg: '#F0FDF4', color: '#16A34A', label: 'High Confidence' },
    medium: { bg: '#FFFBEB', color: '#D97706', label: 'Medium Confidence' },
    low: { bg: '#FEF2F2', color: '#DC2626', label: 'Low Confidence' },
  };
  const s = styles[confidence] || styles.medium;
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>
      {s.label}
    </span>
  );
}

function StandardCard({ standard, index }) {
  const [expanded, setExpanded] = useState(index === 0);

  return (
    <div style={{ border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden', marginBottom: 10 }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#F8FAFC', cursor: 'pointer' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <CheckCircle size={16} color="#16A34A" />
          <div>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: 700, color: '#0A1628' }}>
              {standard.cfr_part} {standard.subpart} — {standard.title}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {standard.regulation} · {standard.applicability === 'new_source' ? 'New Sources' : standard.applicability === 'existing_source' ? 'Existing Sources' : 'New & Existing Sources'}
              {standard.threshold && ` · ${standard.threshold}`}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ConfidenceBadge confidence={standard.confidence} />
          {expanded ? <ChevronUp size={16} color="#94a3b8" /> : <ChevronDown size={16} color="#94a3b8" />}
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '16px', borderTop: '1px solid #E2E8F0' }}>
          <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.65, marginBottom: 16 }}>{standard.summary}</p>

          {standard.key_requirements && standard.key_requirements.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Key Requirements</div>
              {standard.key_requirements.map((req, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#0066FF', marginTop: 6, flexShrink: 0 }} />
                  <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.55 }}>{req}</div>
                </div>
              ))}
            </div>
          )}

          <div className="tool-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {standard.monitoring && (
              <div style={{ background: '#EFF6FF', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#1E40AF', marginBottom: 4 }}>Monitoring</div>
                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.55 }}>{standard.monitoring}</div>
              </div>
            )}
            {standard.recordkeeping && (
              <div style={{ background: '#F0FDF4', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#15803D', marginBottom: 4 }}>Recordkeeping</div>
                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.55 }}>{standard.recordkeeping}</div>
              </div>
            )}
            {standard.notifications && (
              <div style={{ background: '#FFFBEB', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#92400E', marginBottom: 4 }}>Notifications</div>
                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.55 }}>{standard.notifications}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NewSourceForm({ facilities, onAnalyze, analyzing }) {
  const [facilityId, setFacilityId] = useState('');
  const [sources, setSources] = useState([]);
  const [sourceId, setSourceId] = useState('');
  const [useExisting, setUseExisting] = useState(true);
  const [newSource, setNewSource] = useState({ name: '', source_type: '', fuel_type: '', description: '' });

  useEffect(() => {
    if (facilityId) {
      api.get(`/federal-standards/sources/${facilityId}`)
        .then(res => setSources(res.data.sources || []))
        .catch(() => setSources([]));
    }
  }, [facilityId]);

  const handleSubmit = () => {
    if (!facilityId) return toast.error('Select a facility');
    if (useExisting && !sourceId) return toast.error('Select a source');
    if (!useExisting && !newSource.name) return toast.error('Enter source name');
    if (!useExisting && !newSource.source_type) return toast.error('Select source type');

    onAnalyze({
      facilityId,
      sourceId: useExisting ? sourceId : null,
      sourceData: useExisting ? null : newSource
    });
  };

  const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none', background: 'white', boxSizing: 'border-box' };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 };

  return (
    <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, padding: 24, marginBottom: 24 }}>
      <div style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, color: '#0A1628', marginBottom: 20 }}>
        Run Federal Standards Analysis
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Facility</label>
        <select style={inputStyle} value={facilityId} onChange={e => { setFacilityId(e.target.value); setSourceId(''); }}>
          <option value="">Select facility...</option>
          {facilities.map(f => <option key={f.id} value={f.id}>{f.facility_name || f.name}</option>)}
        </select>
      </div>

      {facilityId && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button
              onClick={() => setUseExisting(true)}
              style={{ flex: 1, padding: '8px', border: `2px solid ${useExisting ? '#0066FF' : '#E2E8F0'}`, borderRadius: 8, background: useExisting ? '#EFF6FF' : 'white', color: useExisting ? '#0066FF' : '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
              Use Existing Source
            </button>
            <button
              onClick={() => setUseExisting(false)}
              style={{ flex: 1, padding: '8px', border: `2px solid ${!useExisting ? '#0066FF' : '#E2E8F0'}`, borderRadius: 8, background: !useExisting ? '#EFF6FF' : 'white', color: !useExisting ? '#0066FF' : '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
              Add New Source
            </button>
          </div>

          {useExisting ? (
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Emission Source</label>
              {sources.length === 0 ? (
                <div style={{ padding: '12px', background: '#F8FAFC', borderRadius: 8, fontSize: 13, color: '#64748b' }}>
                  No sources found for this facility. Upload a permit or add sources manually.
                </div>
              ) : (
                <select style={inputStyle} value={sourceId} onChange={e => setSourceId(e.target.value)}>
                  <option value="">Select source...</option>
                  {sources.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.source_id ? `${s.source_id} - ` : ''}{s.name} {s.source_type ? `(${s.source_type})` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ) : (
            <div className="tool-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>Source Name *</label>
                <input style={inputStyle} placeholder="e.g. Boiler #1" value={newSource.name} onChange={e => setNewSource({ ...newSource, name: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Source Type *</label>
                <select style={inputStyle} value={newSource.source_type} onChange={e => setNewSource({ ...newSource, source_type: e.target.value })}>
                  <option value="">Select type...</option>
                  {SOURCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Fuel Type</label>
                <select style={inputStyle} value={newSource.fuel_type} onChange={e => setNewSource({ ...newSource, fuel_type: e.target.value })}>
                  <option value="">Select fuel...</option>
                  {FUEL_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Description / Capacity</label>
                <input style={inputStyle} placeholder="e.g. 50 MMBtu/hr natural gas boiler" value={newSource.description} onChange={e => setNewSource({ ...newSource, description: e.target.value })} />
              </div>
            </div>
          )}
        </>
      )}

      <button
        onClick={handleSubmit}
        disabled={analyzing || !facilityId}
        style={{ display: 'flex', alignItems: 'center', gap: 8, background: analyzing || !facilityId ? '#94a3b8' : '#0066FF', color: 'white', border: 'none', borderRadius: 9, padding: '11px 24px', fontSize: 14, fontWeight: 600, cursor: analyzing || !facilityId ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-body)' }}>
        {analyzing ? <><Loader size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> Analyzing...</> : <><Zap size={15} /> Run Analysis</>}
      </button>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function FederalStandards() {
  const [facilities, setFacilities] = useState([]);
  const [determinations, setDeterminations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [activeResult, setActiveResult] = useState(null);
  const [filterFacility, setFilterFacility] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [facilitiesRes, determinationsRes] = await Promise.all([
        facilityAPI.getAll(),
        api.get('/federal-standards')
      ]);
      setFacilities(facilitiesRes.data.facilities || []);
      setDeterminations(determinationsRes.data.determinations || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async (params) => {
    setAnalyzing(true);
    setActiveResult(null);
    try {
      const res = await api.post('/federal-standards/analyze', params);
      setActiveResult(res.data);
      setDeterminations(prev => [res.data.determination, ...prev]);
      toast.success('Analysis complete');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this determination?')) return;
    try {
      await api.delete(`/federal-standards/${id}`);
      setDeterminations(prev => prev.filter(d => d.id !== id));
      if (activeResult?.determination?.id === id) setActiveResult(null);
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleViewDetermination = (det) => {
    try {
      const analysis = typeof det.standards_found === 'string'
        ? JSON.parse(det.standards_found)
        : det.standards_found;
      setActiveResult({ determination: det, analysis });
    } catch {
      toast.error('Failed to load determination');
    }
  };

  const filtered = filterFacility
    ? determinations.filter(d => d.facility_id === parseInt(filterFacility))
    : determinations;

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#64748b' }}>Loading...</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Shield size={22} color="#0066FF" />
          Federal Standards
        </div>
        <div className="page-subtitle">Determine applicable NSPS, NESHAP, and MACT standards for your emission sources</div>
      </div>

      <div className="tool-layout" style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 24, alignItems: 'start' }}>

        {/* LEFT — Analysis form + history */}
        <div>
          <NewSourceForm facilities={facilities} onAnalyze={handleAnalyze} analyzing={analyzing} />

          {/* History */}
          <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: 700, color: '#0A1628' }}>Previous Determinations</div>
              <select
                value={filterFacility}
                onChange={e => setFilterFacility(e.target.value)}
                style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #E2E8F0', borderRadius: 6, outline: 'none', fontFamily: 'var(--font-body)' }}>
                <option value="">All facilities</option>
                {facilities.map(f => <option key={f.id} value={f.id}>{f.facility_name || f.name}</option>)}
              </select>
            </div>

            {filtered.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                No determinations yet. Run your first analysis above.
              </div>
            ) : (
              filtered.map(det => {
                const isActive = activeResult?.determination?.id === det.id;
                const standards = typeof det.standards_found === 'string' ? JSON.parse(det.standards_found) : det.standards_found;
                const count = standards?.applicable_standards?.length || 0;
                return (
                  <div
                    key={det.id}
                    onClick={() => handleViewDetermination(det)}
                    style={{ padding: '12px 20px', borderBottom: '1px solid #F8FAFC', cursor: 'pointer', background: isActive ? '#EFF6FF' : 'white', borderLeft: isActive ? '3px solid #0066FF' : '3px solid transparent' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0A1628' }}>{det.source_name}</div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                          {det.facility_name} · {det.source_type || 'Unknown type'}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
                          {new Date(det.determined_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ background: count > 0 ? '#EFF6FF' : '#F8FAFC', color: count > 0 ? '#0066FF' : '#64748b', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>
                          {count} standard{count !== 1 ? 's' : ''}
                        </span>
                        <button
                          onClick={e => { e.stopPropagation(); handleDelete(det.id); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: 4, display: 'flex' }}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* RIGHT — Results */}
        <div>
          {analyzing && (
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, padding: 48, textAlign: 'center' }}>
              <Loader size={32} color="#0066FF" style={{ animation: 'spin 0.8s linear infinite', marginBottom: 16 }} />
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, color: '#0A1628', marginBottom: 8 }}>Analyzing Federal Standards</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>Our AI is reviewing NSPS, NESHAP, and MACT applicability for this source. This may take 20-30 seconds.</div>
            </div>
          )}

          {!analyzing && activeResult && (
            <div>
              {/* Summary bar */}
              <div style={{ background: '#0A1628', borderRadius: 12, padding: '16px 20px', marginBottom: 16, display: 'flex', gap: 24 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontFamily: 'var(--font-heading)', fontSize: 28, fontWeight: 800, color: '#0066FF', letterSpacing: -1 }}>
                    {activeResult.analysis?.applicable_standards?.length || 0}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>Applicable</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontFamily: 'var(--font-heading)', fontSize: 28, fontWeight: 800, color: '#94a3b8', letterSpacing: -1 }}>
                    {activeResult.analysis?.not_applicable?.length || 0}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>Not Applicable</div>
                </div>
                <div style={{ flex: 1, textAlign: 'right' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'white' }}>{activeResult.determination?.source_name}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{activeResult.determination?.source_type}</div>
                </div>
              </div>

              {/* Applicable standards */}
              {activeResult.analysis?.applicable_standards?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#16A34A', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircle size={14} /> Applicable Standards
                  </div>
                  {activeResult.analysis.applicable_standards.map((std, i) => (
                    <StandardCard key={i} standard={std} index={i} />
                  ))}
                </div>
              )}

              {/* Not applicable */}
              {activeResult.analysis?.not_applicable?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <XCircle size={14} /> Not Applicable
                  </div>
                  <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden' }}>
                    {activeResult.analysis.not_applicable.map((std, i) => (
                      <div key={i} style={{ padding: '10px 14px', borderBottom: i < activeResult.analysis.not_applicable.length - 1 ? '1px solid #F1F5F9' : 'none', display: 'flex', gap: 10 }}>
                        <XCircle size={14} color="#94a3b8" style={{ flexShrink: 0, marginTop: 2 }} />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{std.subpart} — {std.regulation}</div>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{std.reason}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {activeResult.analysis?.recommendations?.length > 0 && (
                <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#92400E', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <AlertCircle size={14} /> Recommendations
                  </div>
                  {activeResult.analysis.recommendations.map((rec, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#D97706', marginTop: 6, flexShrink: 0 }} />
                      <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.55 }}>{rec}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Disclaimer */}
              <div style={{ background: '#F1F5F9', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.55 }}>
                  This analysis is AI-generated based on source information provided. Results should be verified by a qualified air quality professional before use in regulatory submissions or permit applications.
                </div>
              </div>
            </div>
          )}

          {!analyzing && !activeResult && (
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, padding: 48, textAlign: 'center' }}>
              <Shield size={48} color="#E2E8F0" style={{ marginBottom: 16 }} />
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, color: '#0A1628', marginBottom: 8 }}>Federal Standards Analysis</div>
              <div style={{ fontSize: 13, color: '#64748b', maxWidth: 320, margin: '0 auto' }}>
                Select a facility and source, then run an analysis to determine which NSPS, NESHAP, and MACT standards apply.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
